import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PHONE_TEXTS_ROUTE, TEXT_CONVERSATIONS_PATH, firstLine, formatDateTime, formatPhone, pageKindLineText, phoneThreadHref, recordHref, t,
  type ColumnDef, type ConversationRow, type ConversationsAnswer, type PageSize,
} from '@bms2/shared';
import { CountBadge } from '../../components/composites/CountBadge';
import { ListViewer } from '../../components/composites/ListViewer';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { PageModule } from '../../shell/PageModule';
import { useApi } from '../records/api';
import styles from './TextThreadPage.module.css';

export { PHONE_TEXTS_ROUTE };

/** A conversation as the list draws it: the read's row with each cell's words beside it. */
interface TextsListRow extends ConversationRow {
  id: string;
  latestText: string;
  theirText: string;
  ourText: string;
  personRoute: string | null;
  timeText: string;
}

/**
 * 🔒 The newest text's first line with the unanswered count pill after it, their number, the Person, the time and our number
 * (decision 11860). The pill rides the first column so the compact list at phone width draws it too.
 */
const COLUMNS: readonly ColumnDef[] = [
  { key: 'latestText', label: t('phonePages.col.latest'), role: 'primary', type: 'text', sortable: false },
  { key: 'theirText', label: t('phonePages.col.theirNumber'), role: 'secondary', type: 'text', sortable: false, compactWrap: true },
  { key: 'personName', label: t('phonePages.col.person'), role: 'secondary', type: 'link', linkPattern: ':personRoute', sortable: false },
  { key: 'timeText', label: t('phonePages.col.time'), role: 'secondary', type: 'text', sortable: false, compactWrap: true },
  { key: 'ourText', label: t('phonePages.col.ourNumber'), role: 'secondary', type: 'text', sortable: false, compactWrap: true },
];

/**
 * 🔒 Texts (decision 11860; the owner's P4a): one row per conversation the viewer reads under decision 11782, the
 * General number's in the shared inbox and a rep's own on their number, newest first. A row opens its thread.
 */
export function TextsPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const fetched = useApi<ConversationsAnswer>(TEXT_CONVERSATIONS_PATH, apiBaseUrl);
  const rows: TextsListRow[] = (fetched.state.kind === 'ready' ? fetched.state.data.rows : []).map((row) => ({
    ...row,
    id: `${row.ourNumber}|${row.theirNumber}`,
    latestText: firstLine(row.latestBody),
    theirText: formatPhone(row.theirNumber) || row.theirNumber,
    ourText: row.ourNumber === '' ? t('phonePages.texts.companyNumber') : formatPhone(row.ourNumber),
    personRoute: row.personId ? recordHref('PersonRecord', row.personId) : null,
    timeText: formatDateTime(row.latestAt, zone, dateFormat),
  }));
  return (
    <PageModule title={t('phonePages.texts.title')} subtitle={pageKindLineText({ page: 'named', route: PHONE_TEXTS_ROUTE })} icon="phoneTexts">
      <ListViewer
        recordType={t('phonePages.texts.title')}
        views={[{ id: 'all', name: t('phonePages.texts.title') }]}
        currentViewId="all"
        onViewChange={() => undefined}
        columns={COLUMNS}
        rows={rows}
        idOf={(row) => row.id}
        read={(row, key) => (row as unknown as Record<string, unknown>)[key]}
        sort={null}
        onSortChange={() => undefined}
        sortLocked={t('phonePages.calls.sortLocked')}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        state={fetched.state}
        onRetry={fetched.reload}
        emptyAction={t('phonePages.texts.empty')}
        selectable={false}
        onNavigate={(row) => navigate(phoneThreadHref(row.theirNumber, row.ourNumber))}
        onRoute={(route) => navigate(route)}
        cellMarker={(row, key) => (key === 'latestText' && row.unanswered > 0 ? <span className={styles.count}><CountBadge count={row.unanswered} label={t('phonePages.col.unanswered')} /></span> : null)}
      />
    </PageModule>
  );
}
